import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "./api";
import { useAuth } from "./AuthContext";
import { ArrowLeft, Plus, Trash2, ExternalLink, LogOut, Power, BarChart3, Save, X } from "lucide-react";

const TYPES = [
  ["hotels", "Hoteles"], ["flights", "Vuelos"], ["activities", "Actividades"],
  ["restaurants", "Restaurantes"], ["transfers", "Traslados"], ["transport", "Transporte"],
  ["insurance", "Seguros"], ["other", "Otros"],
];

const empty = {
  name: "", slug: "", domains: "", param_name: "", param_value: "",
  base_url: "", type: "other", logo_url: "", status: "active", notes: "",
};

function ProviderForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState(() => ({
    ...empty,
    ...initial,
    domains: Array.isArray(initial?.domains) ? initial.domains.join(", ") : (initial?.domains || ""),
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isEdit = !!initial?.id;

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        domains: form.domains.split(",").map((d) => d.trim()).filter(Boolean),
      };
      const url = isEdit ? `/admin/affiliates/providers/${initial.id}` : "/admin/affiliates/providers";
      const method = isEdit ? "patch" : "post";
      const { data } = await api[method](url, payload);
      onSave(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white max-w-2xl w-full p-8 space-y-5 my-10" style={{ borderRadius: "2px" }}>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl text-[#0A192F]">{isEdit ? "Editar proveedor" : "Nuevo proveedor"}</h2>
          <button type="button" onClick={onClose} className="text-[#3A506B] hover:text-[#16335A]"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Nombre *" value={form.name} onChange={handleChange("name")} required />
          <Input label="Slug" value={form.slug} onChange={handleChange("slug")} placeholder="booking" />
          <Input label="Dominios (separados por coma) *" value={form.domains} onChange={handleChange("domains")} placeholder="booking.com, booking.es" full required />
          <Input label="Parámetro de afiliación *" value={form.param_name} onChange={handleChange("param_name")} placeholder="aid / partner_id / camref..." required />
          <Input label="Tu ID *" value={form.param_value} onChange={handleChange("param_value")} required />
          <Select label="Tipo" value={form.type} onChange={handleChange("type")} options={TYPES} />
          <Select label="Estado" value={form.status} onChange={handleChange("status")} options={[["active", "Activo"], ["inactive", "Inactivo"]]} />
          <Input label="URL base (opcional)" value={form.base_url} onChange={handleChange("base_url")} placeholder="https://www.booking.com/" full />
          <Input label="Logo URL (opcional)" value={form.logo_url} onChange={handleChange("logo_url")} full />
          <label className="block md:col-span-2">
            <span className="block text-[0.7rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">Notas</span>
            <textarea value={form.notes} onChange={handleChange("notes")} rows={2}
              className="w-full px-4 py-2.5 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
              style={{ borderRadius: "2px" }} />
          </label>
        </div>
        {error && <p className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 text-sm">{error}</p>}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-5 py-3 text-sm text-[#3A506B]">Cancelar</button>
          <button type="submit" disabled={saving}
            className="bg-[#16335A] hover:bg-[#214A82] disabled:opacity-60 text-white px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2">
            <Save size={14} /> {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Input({ label, full, ...rest }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="block text-[0.7rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">{label}</span>
      <input {...rest} className="w-full px-4 py-2.5 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
        style={{ borderRadius: "2px" }} />
    </label>
  );
}
function Select({ label, options, ...rest }) {
  return (
    <label className="block">
      <span className="block text-[0.7rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">{label}</span>
      <select {...rest} className="w-full px-4 py-2.5 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
        style={{ borderRadius: "2px" }}>
        {options.map(([k, l]) => (<option key={k} value={k}>{l}</option>))}
      </select>
    </label>
  );
}

export default function AdminAffiliates() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [editing, setEditing] = useState(null); // null = none, {} = new, {id...} = edit
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/affiliates/providers");
      setProviders(data);
    } catch (err) { setError(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (p) => {
    try {
      await api.patch(`/admin/affiliates/providers/${p.id}`, { status: p.status === "active" ? "inactive" : "active" });
      load();
    } catch (err) { alert(formatApiError(err)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar el proveedor "${p.name}"?`)) return;
    try { await api.delete(`/admin/affiliates/providers/${p.id}`); load(); }
    catch (err) { alert(formatApiError(err)); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-[#E8C97A]">
            <ArrowLeft size={14} /> Volver
          </button>
          <div className="flex items-center gap-5">
            <Link to="/admin/stats" className="text-sm text-white/80 hover:text-[#E8C97A] inline-flex items-center gap-2"><BarChart3 size={14} /> Estadísticas</Link>
            <span className="hidden sm:inline text-sm text-white/70">{user?.email}</span>
            <button onClick={logout} className="text-sm text-white/70 hover:text-[#E8C97A] inline-flex items-center gap-2"><LogOut size={14} /> Salir</button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <span className="label-eyebrow text-[#16335A]/70">Motor de afiliación</span>
            <h1 className="mt-2 font-display text-3xl lg:text-5xl text-[#0A192F] tracking-tight">
              Centro de <span className="italic text-[#16335A]">Afiliados</span>
            </h1>
            <p className="mt-2 text-[#3A506B] max-w-2xl">
              Tus proveedores e IDs. Cualquier enlace que pegues en una propuesta se valida con estas reglas.
            </p>
          </div>
          <button onClick={() => setEditing({})}
            className="bg-[#16335A] hover:bg-[#214A82] text-white px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2">
            <Plus size={16} /> Nuevo proveedor
          </button>
        </div>
        {error && <p className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 mb-6 text-sm">{error}</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {providers.map((p) => (
            <div key={p.id} className={`bg-white border p-5 transition-all ${p.status === "active" ? "border-[#16335A]/15" : "border-[#16335A]/10 opacity-70"}`}
              style={{ borderRadius: "2px" }} data-testid={`provider-card-${p.slug}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-xl text-[#0A192F]">{p.name}</h3>
                  <p className="mt-0.5 text-xs text-[#3A506B]">{(p.domains || []).join(", ")}</p>
                </div>
                <button onClick={() => toggle(p)} title={p.status === "active" ? "Desactivar" : "Activar"}
                  className={`p-1.5 rounded-full ${p.status === "active" ? "text-green-600 hover:bg-green-50" : "text-[#3A506B] hover:bg-[#FDFBF7]"}`}>
                  <Power size={14} />
                </button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <div><div className="text-[#16335A]/60 uppercase tracking-wider text-[0.6rem]">Parámetro</div><div className="font-mono">{p.param_name || "—"}</div></div>
                <div><div className="text-[#16335A]/60 uppercase tracking-wider text-[0.6rem]">Tu ID</div><div className="font-mono">{p.param_value || <em className="text-red-600 not-italic">⚠ vacío</em>}</div></div>
              </div>
              <div className="mt-5 flex items-center justify-between">
                <button onClick={() => setEditing(p)} className="text-xs text-[#16335A] hover:underline">Editar</button>
                <div className="flex items-center gap-3">
                  {p.base_url && (<a href={p.base_url} target="_blank" rel="noreferrer" className="text-[#16335A]/60 hover:text-[#16335A]"><ExternalLink size={12} /></a>)}
                  <button onClick={() => remove(p)} className="text-red-600/70 hover:text-red-700"><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
      {editing !== null && (
        <ProviderForm
          initial={editing}
          onSave={() => { setEditing(null); load(); }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
